<template>
  <div class="voice-clone-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:microphone" width="24" height="24" />
        AI 声音克隆
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="search.keyword" placeholder="按名称/音色ID搜索" clearable size="large" class="search-input"
            @keyup.enter="doSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="doSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>

          <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 条记录吗？`"
            @confirm="doBatchDelete">
            <template #reference>
              <el-button type="danger" :disabled="!selectedIds.length" size="large">
                <Icon icon="heroicons:trash" width="18" height="18" />
                <span class="btn-text">批量删除（{{ selectedIds.length }}）</span>
              </el-button>
            </template>
          </el-popconfirm>
        </div>

        <el-button type="success" size="large" @click="openAdd" class="add-btn">
          <Icon icon="heroicons:sparkles" width="18" height="18" />
          <span class="btn-text">AI 克隆</span>
        </el-button>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table :data="list" stripe v-loading="loading" class="data-table" @selection-change="handleSelectionChange"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">

          <el-table-column type="selection" width="48" />
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="voice_name" label="名称" width="200" show-overflow-tooltip />
          <el-table-column prop="voice_describe" label="克隆描述" min-width="200" show-overflow-tooltip />
          <el-table-column prop="voice_id" label="音色ID" min-width="240" show-overflow-tooltip />

          <el-table-column label="音频素材" min-width="320">
            <template #default="{ row }">
              <div class="audio-list">
                <div v-for="(u, idx) in splitByAnyComma(row.human_voice_url).slice(0, 2)" :key="u + idx"
                  class="audio-item">
                  <audio :src="u" controls preload="metadata" class="audio-player"></audio>
                  <button class="icon-btn" @click="copyText(u)">
                    <Icon icon="heroicons:document-duplicate" width="14" height="14" />
                  </button>
                </div>

                <el-popover v-if="splitByAnyComma(row.human_voice_url).length > 2" placement="bottom" width="400"
                  trigger="click">
                  <template #reference>
                    <button class="more-btn">
                      <Icon icon="heroicons:dots-horizontal" width="14" height="14" />
                      更多 {{ splitByAnyComma(row.human_voice_url).length - 2 }}
                    </button>
                  </template>
                  <div class="audio-popover">
                    <div v-for="(u, idx) in splitByAnyComma(row.human_voice_url).slice(2)" :key="u + 'more' + idx"
                      class="audio-item">
                      <audio :src="u" controls preload="metadata" class="audio-player"></audio>
                      <button class="icon-btn" @click="copyText(u)">
                        <Icon icon="heroicons:document-duplicate" width="14" height="14" />
                      </button>
                    </div>
                  </div>
                </el-popover>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="性别" width="80">
            <template #default="{ row }">
              <span class="type-tag" :class="row.voice_sex === 2 ? 'gender-female' : 'gender-male'">
                {{ row.voice_sex === 2 ? '女' : '男' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="状态" width="90">
            <template #default="{ row }">
              <span class="type-tag" :class="row.status === 1 ? 'status-success' : 'status-failed'">
                {{ row.status === 1 ? '正常' : '失败' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column prop="create_time" label="克隆时间" width="170" />

          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <button class="action-btn danger-btn" @click="delOne(row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.pageSize" :total="pager.total"
          :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout" @size-change="fetchList"
          @current-change="fetchList" background />
      </div>
    </div>

    <!-- 新增克隆弹窗 -->
    <el-dialog v-model="dialogVisible" title="AI 声音克隆" :width="dialogWidth" :close-on-click-modal="false"
      destroy-on-close class="custom-dialog">
      <el-form ref="formRef" :model="form" :rules="rules" :label-width="formLabelWidth" class="clone-form">
        <el-form-item label="自定义名称" prop="voice_name">
          <el-input v-model="form.voice_name" placeholder="支持多个，用中文/英文逗号分隔" size="large">
            <template #append>
              <button class="input-append-btn" @click="insertSep('voice_name')" type="button">
                <Icon icon="heroicons:plus" width="14" height="14" />
                插入分隔符
              </button>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item label="克隆描述" prop="voice_describe">
          <el-input v-model="form.voice_describe" placeholder="必填；支持多个，用中文/英文逗号分隔" size="large">
            <template #append>
              <button class="input-append-btn" @click="insertSep('voice_describe')" type="button">
                <Icon icon="heroicons:plus" width="14" height="14" />
                插入分隔符
              </button>
            </template>
          </el-input>
          <div class="form-hint">示例：温暖、自然；或：讲解型、磁性；长度不限，建议简洁准确</div>
        </el-form-item>

        <el-form-item label="音频素材" prop="human_voice_url">
          <el-input v-model="form.human_voice_url" type="textarea" :rows="2" class="mono-input"
            placeholder="粘贴音频直链，支持多个，用英文逗号 , 分隔" />
          <div class="upload-area">
            <el-upload class="drop-card" drag multiple :auto-upload="false" :show-file-list="false"
              accept=".mp3,.wav,.m4a,.aac" :on-change="onPickLocalAudio">
              <div class="drop-inner">
                <div class="drop-icon">
                  <Icon icon="heroicons:cloud-arrow-up" width="32" height="32" />
                </div>
                <div class="drop-title">将音频拖到此处</div>
                <div class="drop-sub">或 <span class="link">点击选择</span>（可多选）</div>
                <div class="drop-meta">建议原始时长 ≥ 10 秒</div>
              </div>
            </el-upload>

            <div v-if="uploadedUrls.length" class="url-chips">
              <span v-for="(u, i) in uploadedUrls" :key="u" class="url-chip">
                {{ truncateUrl(u) }}
                <button class="chip-close" @click="removeUrl(i)">
                  <Icon icon="heroicons:x-mark" width="12" height="12" />
                </button>
              </span>
            </div>

            <div v-if="queue.length" class="queue">
              <div class="queue-title">上传进度</div>
              <div v-for="q in queue" :key="q.id" class="queue-item">
                <span class="q-name" :title="q.name">{{ q.name }}</span>
                <el-progress :percentage="q.percent" :status="q.status" :stroke-width="6" />
              </div>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="性别">
          <div class="radio-group">
            <label class="radio-item" :class="{ active: form.voice_sex === 1 }">
              <input type="radio" v-model="form.voice_sex" :value="1" />
              <span>男</span>
            </label>
            <label class="radio-item" :class="{ active: form.voice_sex === 2 }">
              <input type="radio" v-model="form.voice_sex" :value="2" />
              <span>女</span>
            </label>
          </div>
        </el-form-item>

        <el-form-item>
          <label class="checkbox-label">
            <input type="checkbox" v-model="agree" :disabled="!agreeEnabled" />
            <span>我已阅读并同意</span>
            <button class="link-btn" type="button" @click.prevent="openAgreement">
              《克隆使用协议》
            </button>
          </label>
          <span v-if="agreeCountdown > 0" class="countdown-text">（请至少阅读 {{ agreeCountdown }} 秒）</span>
        </el-form-item>

        <div class="price-tip">
          <Icon icon="heroicons:information-circle" width="16" height="16" />
          {{ priceTip }}
        </div>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" :disabled="!agreeReady || urlList.length === 0 || submitting"
          @click="submit">
          <Icon icon="heroicons:sparkles" width="16" height="16" />
          {{ submitBtnText }}
        </button>
      </template>
    </el-dialog>

    <!-- 协议弹窗 -->
    <el-dialog v-model="agreementDialogVisible" :title="agreementTitle || '克隆使用协议'" :width="dialogWidth"
      :close-on-click-modal="false" @closed="stopAgreeCountdown" class="custom-dialog">
      <div v-loading="agreementLoading" class="agreement-content">
        <div class="agreement-meta" v-if="agreementUpdatedAt">
          最近更新：{{ agreementUpdatedAt }}
        </div>
        <div class="agreement-text" v-if="agreementContent" v-html="agreementContent"></div>
        <el-empty v-else description="未读取到协议内容" />
      </div>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="agreementDialogVisible = false">关闭</button>
        <button class="dialog-btn confirm-btn" :disabled="agreeCountdown > 0" @click="acceptAgreement">
          {{ agreeCountdown > 0 ? `请阅读（${agreeCountdown}s）` : '我已阅读并同意' }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import { Icon } from '@iconify/vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getUploadSignature, uploadToOSS, beforeUpload, getVideoDuration } from '@/utils/uploadUtils'

export default {
  name: 'VoiceClone',
  components: { Icon },
  data() {
    return {
      loading: false,
      list: [],
      pager: { page: 1, pageSize: 10, total: 0 },
      search: { keyword: '' },
      selectedIds: [],
      dialogVisible: false,
      submitting: false,
      agree: false,
      agreeEnabled: false,
      agreeCountdown: 0,
      agreeTimer: null,
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementUpdatedAt: '',
      agreementContent: '',
      processedUids: new Set(),
      queue: [],
      uploadedUrls: [],
      form: {
        voice_name: '',
        voice_describe: '',
        human_voice_url: '',
        voice_sex: 1
      },
      rules: {
        voice_name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        voice_describe: [{ required: true, message: '请填写克隆描述（必填）', trigger: 'blur' }],
        human_voice_url: [{ required: true, message: '请填写音频直链', trigger: 'blur' }]
      },
      unitPrice: 0,
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    urlList() {
      return this.splitByAnyComma(this.form.human_voice_url)
    },
    nameList() {
      return this.splitByAnyComma(this.form.voice_name)
    },
    descList() {
      return this.splitByAnyComma(this.form.voice_describe)
    },
    totalCost() {
      return ((this.unitPrice || 0) * (this.urlList.length || 0)).toFixed(2)
    },
    priceTip() {
      return `AI克隆（${this.unitPrice} 积分/次），本次预计消耗：${this.unitPrice} × ${this.urlList.length || 0} = ${this.totalCost} 积分`
    },
    submitBtnText() {
      return `AI 克隆（预计 ${this.totalCost} 积分）`
    },
    agreeReady() {
      return this.agree && this.agreeCountdown === 0
    },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '800px'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '100px'
    }
  },
  mounted() {
    this.fetchList()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.stopAgreeCountdown()
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
    truncateUrl(url) {
      if (!url) return ''
      if (url.length > 40) {
        return url.substring(0, 25) + '...' + url.substring(url.length - 12)
      }
      return url
    },
    async fetchList() {
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, {
          action: this.search.keyword ? 'search' : 'list',
          store_id: this.userInfo.id,
          keyword: this.search.keyword,
          page: this.pager.page,
          page_size: this.pager.pageSize
        })
        this.list = data.data || []
        this.pager.total = data.pagination ? data.pagination.total : (this.list?.length || 0)
        if (!this.list.length && this.pager.page > 1) {
          this.pager.page = this.pager.page - 1
          await this.fetchList()
        }
        this.selectedIds = []
      } catch (e) {
        ElMessage.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },
    copyText(text) {
      if (!text) return
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => ElMessage.success('已复制链接'))
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        try {
          document.execCommand('copy')
          ElMessage.success('已复制链接')
        } catch (e) {
          ElMessage.error('复制失败')
        } finally {
          document.body.removeChild(ta)
        }
      }
    },
    doSearch() {
      this.pager.page = 1
      this.fetchList()
    },
    handleSelectionChange(rows) {
      this.selectedIds = rows.map(r => r.id)
    },
    async doBatchDelete() {
      if (!this.selectedIds.length) return
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, {
          action: 'batch_delete',
          store_id: this.userInfo.id,
          ids: this.selectedIds
        })
        if (data.success) {
          ElMessage.success('批量删除成功')
          await this.fetchList()
        } else {
          ElMessage.error(data.error || '批量删除失败')
        }
      } catch (e) {
        ElMessage.error('批量删除失败')
      } finally {
        this.loading = false
      }
    },
    async openAdd() {
      this.dialogVisible = true
      this.agree = false
      this.agreeEnabled = false
      this.stopAgreeCountdown()
      this.form = { voice_name: '', voice_describe: '', human_voice_url: '', voice_sex: 1 }
      this.uploadedUrls = []
      this.queue = []
      this.processedUids.clear()
      this.$nextTick(() => this.$refs.formRef?.clearValidate?.())
      try {
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_digital_human_config.php`)
        const v = Number(parseFloat(data?.data?.digital_human_voice_clone_num || 0).toFixed(2))
        this.unitPrice = isNaN(v) ? 0 : v
      } catch {
        this.unitPrice = 0
      }
    },
    insertSep(field) {
      const val = (this.form[field] ?? '').toString()
      const endsWithComma = /[，,]\s*$/.test(val)
      this.form[field] = endsWithComma ? `${val} ` : `${val}${val ? ', ' : ''}`
    },
    splitByAnyComma(str) {
      return (str || '')
        .split(/[，,]/g)
        .map(s => s.trim())
        .filter(Boolean)
    },
    async onPickLocalAudio(uploadFile) {
      try {
        const uid = uploadFile?.uid
        const file = uploadFile?.raw || uploadFile
        if (!file) return
        if (uid && this.processedUids.has(uid)) return
        if (uid) this.processedUids.add(uid)

        const q = {
          id: uid || `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          name: file.name || '未命名音频',
          percent: 0,
          status: undefined
        }
        this.queue.push(q)

        const ok = await beforeUpload(file, { type: 'audio' })
        if (!ok) {
          q.status = 'exception'
          return
        }
        q.percent = 10

        try {
          const secs = await getVideoDuration(file)
          if (!isNaN(secs) && secs < 5) {
            ElMessage.warning(`${q.name} 时长较短（≈${secs.toFixed(1)}s），建议 ≥ 10 秒以保证质量`)
          }
        } catch (e) {
          console.debug('getVideoDuration failed', e)
        }

        q.percent = 45
        const sign = await getUploadSignature('audio')
        const res = await uploadToOSS(file, sign)
        q.percent = 90

        if (res?.success && res.url) {
          this.uploadedUrls.push(res.url)
          const manual = (this.form.human_voice_url || '').split(',').map(s => s.trim()).filter(Boolean)
          const total = Array.from(new Set([...manual, ...this.uploadedUrls]))
          this.form.human_voice_url = total.join(',')
          q.percent = 100
          q.status = 'success'
          ElMessage.success(`上传成功：${q.name}`)
        } else {
          q.status = 'exception'
          ElMessage.error(`上传失败：${q.name}`)
        }
      } catch (err) {
        console.error(err)
        ElMessage.error(err?.message || '上传失败')
      }
    },
    removeUrl(idx) {
      this.uploadedUrls.splice(idx, 1)
      const manual = this.splitByAnyComma(this.form.human_voice_url)
      const remained = manual.filter(u => this.uploadedUrls.includes(u))
      this.form.human_voice_url = remained.join(',')
    },
    async openAgreement() {
      try {
        this.agreementDialogVisible = true
        this.agreementLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_clone_agreement.php`)
        if (data?.code === 200 && data?.data) {
          this.agreementTitle = data.data.title || '克隆使用协议'
          this.agreementUpdatedAt = data.data.updated_at || ''
          this.agreementContent = data.data.content || ''
        } else {
          this.agreementTitle = '克隆使用协议'
          this.agreementUpdatedAt = ''
          this.agreementContent = ''
        }
      } catch (e) {
        ElMessage.error('读取使用协议失败')
        this.agreementContent = ''
      } finally {
        this.agreementLoading = false
        this.startAgreeCountdown(3)
      }
    },
    startAgreeCountdown(sec) {
      this.stopAgreeCountdown()
      this.agreeCountdown = sec
      this.agreeTimer = setInterval(() => {
        if (this.agreeCountdown > 0) this.agreeCountdown--
        if (this.agreeCountdown <= 0) this.stopAgreeCountdown()
      }, 1000)
    },
    stopAgreeCountdown() {
      if (this.agreeTimer) clearInterval(this.agreeTimer)
      this.agreeTimer = null
    },
    acceptAgreement() {
      if (this.agreeCountdown > 0) return
      this.agreementDialogVisible = false
      this.agreeEnabled = true
      this.agree = true
    },
    async submit() {
      this.$refs.formRef.validate(async (ok) => {
        if (!ok) return
        if (!this.agreeReady) {
          ElMessage.warning('请先阅读并同意克隆使用协议')
          return
        }
        if (this.urlList.length === 0) {
          ElMessage.warning('请至少提供一个音频直链')
          return
        }
        this.submitting = true
        try {
          const payload = {
            action: 'add',
            store_id: this.userInfo.id,
            voice_name: this.nameList.length > 1 ? this.nameList : (this.nameList[0] || ''),
            voice_describe: this.descList.length > 1 ? this.descList : (this.descList[0] || ''),
            human_voice_url: this.urlList.length > 1 ? this.urlList : (this.urlList[0] || ''),
            voice_sex: this.form.voice_sex
          }
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, payload)
          if (data.success) {
            ElMessage.success('已提交克隆任务')
            this.dialogVisible = false
            this.fetchList()
          } else {
            ElMessage.error(data.error || '提交失败')
          }
        } catch (e) {
          ElMessage.error('提交失败')
        } finally {
          this.submitting = false
        }
      })
    },
    async delOne(id) {
      try {
        await ElMessageBox.confirm('确定删除该记录吗？', '提示', { type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消' })
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, {
          action: 'delete',
          id,
          store_id: this.userInfo.id
        })
        if (data.success) {
          ElMessage.success('已删除')
          this.fetchList()
        } else {
          ElMessage.error(data.error || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('删除失败')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.voice-clone-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

:deep(.data-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;

  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  .el-table-fixed-column--right {
    background-color: #120e21 !important;
    backdrop-filter: none !important;
  }

  th.el-table-fixed-column--right {
    background-color: #2a1f45 !important;
  }

  .el-table__row--striped .el-table-fixed-column--right {
    background-color: #1a1430 !important;
  }

  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }
}

.type-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;

  &.gender-male {
    background: rgba(56, 189, 248, 0.15);
    color: #38bdf8;
  }

  &.gender-female {
    background: rgba(244, 114, 182, 0.15);
    color: #f472b6;
  }

  &.status-success {
    background: rgba(74, 222, 128, 0.15);
    color: #4ade80;
  }

  &.status-failed {
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
  }
}

.audio-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.audio-item {
  display: flex;
  align-items: center;
  gap: 8px;

  .audio-player {
    height: 36px;
    width: 180px;
    border-radius: 20px;
  }
}

.more-btn {
  background: rgba(157, 78, 221, 0.15);
  border: 1px solid rgba(157, 78, 221, 0.4);
  color: #c084fc;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: rgba(157, 78, 221, 0.25);
  }
}

.audio-popover {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
}

.icon-btn {
  background: rgba(157, 78, 221, 0.15);
  border: none;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: #c084fc;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(157, 78, 221, 0.25);
  }
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &.danger-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
    }
  }

  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }

  @media (max-width: 576px) {
    .btn-prev,
    .btn-next,
    .el-pager li {
      min-width: 28px;
      height: 28px;
      margin: 0 2px;
    }
  }
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }

  @media (max-width: 576px) {
    .el-dialog__header {
      padding: 16px;
    }
    .el-dialog__body {
      padding: 16px;
    }
    .el-dialog__footer {
      padding: 12px 16px;
    }
  }
}

.clone-form {
  :deep(.el-form-item__label) {
    color: #a0a0c0;
    font-weight: 500;
  }

  :deep(.el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    box-shadow: none;

    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }

    &.is-focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }

    .el-input__inner {
      color: #fff;
      background: transparent;
    }
  }

  :deep(.el-textarea__inner) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    color: #fff;

    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }

    &:focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }
  }
}

.input-append-btn {
  background: rgba(157, 78, 221, 0.2);
  border: none;
  color: #c084fc;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;

  &:hover {
    background: rgba(157, 78, 221, 0.3);
  }
}

.radio-group {
  display: flex;
  gap: 20px;

  .radio-item {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: #a0a0c0;

    input {
      cursor: pointer;
      accent-color: #9d4edd;
    }

    &.active span {
      color: #c084fc;
    }
  }
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #a0a0c0;
  font-size: 13px;

  input {
    cursor: pointer;
    accent-color: #9d4edd;
  }
}

.link-btn {
  background: none;
  border: none;
  color: #c084fc;
  cursor: pointer;
  padding: 0;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
}

.form-hint {
  font-size: 12px;
  color: #7a7a9a;
  margin-top: 4px;
}

.countdown-text {
  margin-left: 8px;
  font-size: 12px;
  color: #fbbf24;
}

.price-tip {
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 10px 14px;
  background: rgba(157, 78, 221, 0.1);
  border-radius: 10px;
  font-size: 13px;
  color: #a0a0c0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-area {
  width: 100%;
  margin-top: 8px;
}

.drop-card {
  border: 1px dashed rgba(157, 78, 221, 0.4);
  border-radius: 12px;
  background: rgba(26, 21, 53, 0.6);
  transition: all 0.2s;

  &:hover {
    border-color: #9d4edd;
    background: rgba(157, 78, 221, 0.1);
  }

  :deep(.el-upload-dragger) {
    background: transparent;
    border: none;
  }
}

.drop-inner {
  padding: 24px 16px;
  text-align: center;
}

.drop-icon {
  color: #9d4edd;
  margin-bottom: 8px;
}

.drop-title {
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
}

.drop-sub {
  font-size: 13px;
  color: #a0a0c0;

  .link {
    color: #c084fc;
    cursor: pointer;
  }
}

.drop-meta {
  font-size: 11px;
  color: #7a7a9a;
  margin-top: 8px;
}

.url-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.url-chip {
  background: rgba(157, 78, 221, 0.15);
  border-radius: 16px;
  padding: 4px 8px 4px 12px;
  font-size: 11px;
  color: #a0a0c0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .chip-close {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border-radius: 50%;
    color: #a0a0c0;

    &:hover {
      color: #f87171;
    }
  }
}

.mono-input :deep(textarea) {
  font-family: monospace;
}

.queue {
  margin-top: 12px;
  max-height: 200px;
  overflow-y: auto;

  .queue-title {
    font-weight: 600;
    color: #fff;
    margin-bottom: 8px;
    font-size: 13px;
  }

  .queue-item {
    margin-bottom: 10px;

    .q-name {
      display: block;
      font-size: 11px;
      color: #a0a0c0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 4px;
    }
  }
}

.agreement-content {
  max-height: 60vh;
  overflow: auto;

  .agreement-meta {
    color: #7a7a9a;
    font-size: 12px;
    margin-bottom: 12px;
  }

  .agreement-text {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.7;
    font-size: 13px;
    color: #a0a0c0;
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }
}

:deep(.el-progress-bar__outer) {
  background: rgba(157, 78, 221, 0.2);
}

:deep(.el-progress-bar__inner) {
  background: linear-gradient(90deg, #6546b4, #9d4edd);
}

:deep(.el-checkbox__inner) {
  background: rgba(26, 21, 53, 0.8);
  border-color: rgba(157, 78, 221, 0.4);

  &:hover {
    border-color: #9d4edd;
  }
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background: #9d4edd;
  border-color: #9d4edd;
}

:deep(.el-empty__description p) {
  color: #a0a0c0;
}

@media (max-width: 576px) {
  .btn-text {
    display: none;
  }

  .action-btn {
    padding: 5px 8px;
  }

  .audio-player {
    max-width: 140px;
  }
}
</style>